import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import {
  formatMahnungDate,
  listMahnungen,
  mahnungStatusLabel,
  type MahnungListItem,
} from "@/lib/mahnwesen";

/** Zahlungserinnerungen und Mahnungen in der Dokumentenliste. */
export function MahnungDokumenteList({
  q,
  status,
  datumVon,
  datumBis,
}: {
  q: string;
  status: string;
  datumVon: string;
  datumBis: string;
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState<MahnungListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listMahnungen({
      q: q.trim() || undefined,
      status: status || undefined,
      von: datumVon || undefined,
      bis: datumBis || undefined,
      limit: 300,
    })
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setItems([]);
          setError(err instanceof ApiError ? err.message : "Mahnungen konnten nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [q, status, datumVon, datumBis]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-label-secondary">
        <Spinner />
        <span className="ml-2 text-sm">Mahnungen werden geladen …</span>
      </div>
    );
  }
  if (error) {
    return <p className="px-1 py-6 text-sm text-status-red">{error}</p>;
  }
  if (!items.length) {
    return <p className="px-1 py-6 text-sm text-label-secondary">Keine Mahnungen für diesen Filter.</p>;
  }

  return (
    <div className="overflow-hidden rounded-ios-lg bg-card shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-separator bg-fill-tertiary/40 text-xs uppercase tracking-wide text-label-secondary">
            <tr>
              <th className="px-3 py-2.5 font-semibold">Nummer</th>
              <th className="px-3 py-2.5 font-semibold">Stufe</th>
              <th className="px-3 py-2.5 font-semibold">Kunde</th>
              <th className="px-3 py-2.5 font-semibold">Rechnungen</th>
              <th className="px-3 py-2.5 font-semibold">Datum</th>
              <th className="px-3 py-2.5 font-semibold text-right">Gesamt</th>
              <th className="px-3 py-2.5 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr
                key={m.id}
                className="cursor-pointer border-b border-separator/70 last:border-0 hover:bg-fill-tertiary/30"
                onClick={() => navigate(`/dokumente/mahnwesen/${m.id}`)}
              >
                <td className="px-3 py-3 align-top font-medium text-accent">
                  {m.nummer || `Entwurf #${m.id}`}
                </td>
                <td className="px-3 py-3 align-top">{m.stufe_bezeichnung}</td>
                <td className="px-3 py-3 align-top">{m.kunde_name || "—"}</td>
                <td className="px-3 py-3 align-top text-xs text-label-secondary">
                  {m.rechnungsnummern || "—"}
                </td>
                <td className="px-3 py-3 align-top tabular-nums">{formatMahnungDate(m.belegdatum)}</td>
                <td className="px-3 py-3 align-top text-right font-medium tabular-nums">
                  {formatCurrency(m.summe_gesamt, "EUR")}
                </td>
                <td className="px-3 py-3 align-top">{mahnungStatusLabel(m.status, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
